import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import classNames from "classnames";
import { ProductShippingRatesDocument, ProductShippingRatesQuery, ProductShippingRatesQueryVariables } from "@/generated/graphql/b2c";
import { useServices } from "@/services-context";
import { COUNTRIES_LISTENER_NAME, defaultCountryCode } from "@/services/countries.ts";
import { CUSTOMER_CHANNEL_LISTENER_NAME } from "@/services/customer-channel.ts";
import { FixButton } from "@/elements-react/fix-button";
import { FixDropdown, FixDropdownOptionItem } from "@/elements-react/fix-dropdown";
import { CurrencyConverter } from "@/util/currency-converter.ts";
import { LayoutStyleTypes } from "@/util-react/layout";

export interface ShippingRatesProps {
  className?: string;
  productId: string;
  chainHash?: string | null;
  quantity: number;
  type?: LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
}

type ShippingRateMethod = ProductShippingRatesQuery["ProductShippingRates"]["shippingMethods"][number];
type RequestStatus = "idle" | "loading" | "success" | "error";

const getInitialCountryCode = (services: ReturnType<typeof useServices>) => {
  return (
    services.parameters.getVisibleCountry() ??
    services.customerChannel.currentCountry?.code ??
    services.countries.getCurrentCountryCode() ??
    defaultCountryCode
  ).toUpperCase();
};

const isPricedShippingMethod = (method: ShippingRateMethod | null | undefined): method is ShippingRateMethod => {
  return Boolean(method?.id && method.price);
};

const ShippingRates: React.FC<ShippingRatesProps> = ({ className, productId, chainHash, quantity, type = LayoutStyleTypes.TYPE_LG }) => {
  const { t } = useTranslation();
  const services = useServices([COUNTRIES_LISTENER_NAME, CUSTOMER_CHANNEL_LISTENER_NAME]);
  const channelCountries = services.customerChannel.availableCountries ?? [];
  const [countryCode, setCountryCode] = useState(() => getInitialCountryCode(services));
  const [shippingMethods, setShippingMethods] = useState<ShippingRateMethod[]>([]);
  const [status, setStatus] = useState<RequestStatus>("idle");

  useEffect(() => {
    void services.countries.fetchAllCountries();
    void services.currency.fetchAllCurrencies();
  }, [services]);

  useEffect(() => {
    setShippingMethods([]);
    setStatus("idle");
  }, [productId, chainHash, quantity, countryCode]);

  const countryOptions: FixDropdownOptionItem[] = useMemo(
    () =>
      channelCountries
        .map((country) => ({
          value: country.code,
          label: country.name,
          selected: country.code === countryCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [channelCountries, countryCode],
  );

  const getRates = useCallback(async () => {
    if (!chainHash || !countryCode || status === "loading") {
      return;
    }

    setStatus("loading");

    const response = await services.api.client.query<ProductShippingRatesQuery, ProductShippingRatesQueryVariables>({
      query: ProductShippingRatesDocument,
      variables: {
        input: {
          countryCode,
          products: [
            {
              id: productId,
              chainHash,
              quantity,
            },
          ],
        },
      },
    });

    if (response.error) {
      setStatus("error");
      setShippingMethods([]);
      return;
    }

    setShippingMethods((response.data.ProductShippingRates?.shippingMethods ?? []).filter(isPricedShippingMethod));
    setStatus("success");
  }, [services, productId, chainHash, quantity, countryCode, status]);

  const formatPrice = useCallback(
    (method: ShippingRateMethod) => {
      const currencyCode = services.formatter.getCurrentCurrencyCode();
      const rate = services.currency.getRate(currencyCode) ?? 1;
      const convertedPrice = CurrencyConverter.convert(method.price, currencyCode, rate);

      return services.formatter.formatMoney(convertedPrice, "full", false);
    },
    [services],
  );

  const renderRates = () => {
    if (status === "idle") {
      return null;
    }

    if (status === "loading") {
      return <div className={styles.message}>{t("w3ifh92mg8q6lq1t.loading-rates", "Loading shipping rates...")}</div>;
    }

    if (status === "error") {
      return <div className={styles.message}>{t("gh23xjcf78nv3hca.rates-error", "Shipping rates are temporarily unavailable")}</div>;
    }

    if (!shippingMethods.length) {
      return <div className={styles.message}>{t("q7t1vdy9e0fsq9kg.no-rates", "No shipping rates available for this country")}</div>;
    }

    return (
      <div className={styles.rates}>
        {shippingMethods.map((method) => (
          <div className={styles.rate} key={method.id}>
            <div className={styles.method}>{method.name}</div>
            <div className={styles.leadTime}>{method.leadTime}</div>
            <div className={styles.price}>{formatPrice(method)}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className={classNames(styles.root, styles[`type-${type}`], className)}>
      <h2 className={styles.title}>{t("p6jx7w3dj9b3n1ng.shipping-rates", "Shipping rates")}</h2>
      <div className={styles.controls}>
        <span className={styles.label}>{t("ko68f02z3gq74umf.shipping-to", "Shipping to:")}</span>
        <div className={styles.country}>
          <FixDropdown
            controlled
            isSearchable
            items={countryOptions}
            onSelect={(value) => setCountryCode(String(value))}
          />
        </div>
        <FixButton className={styles.button} variant="primary" size="large" loading={status === "loading"} disabled={!chainHash} onClick={getRates}>
          {t("w4e17ksw5f2n5bv7.get-rates", "Get rates")}
        </FixButton>
      </div>
      {renderRates()}
    </section>
  );
};

export default ShippingRates;
